/**
 * @fileoverview AiQuickEntryDialog — describe your time, review the form
 *
 * Purpose: The plain-English entry box from the timesheets page, available
 * from anywhere in the app via the header "Quick entry" button. Parsing does
 * not save anything: the results are loaded into the Log time dialog so the
 * practitioner reads the coded matter, category and units before committing.
 *
 * When a sentence describes several pieces of work, the first is loaded and
 * the rest queue up behind it (see `openQuickEntryBatch`).
 *
 * Mounted once in the dashboard layout, beside `QuickTimeEntryDialog`.
 */

import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";

import { useActiveOrg } from "@/utils/org-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { NlEntryComposer, type ParsedEntry } from "./NlEntryComposer";
import { useTimeTracker } from "./TimeTrackerContext";

function todayIso(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function toPrefill(parsed: ParsedEntry, fallbackJobId?: string) {
  const parsedJobId = parsed.jobId as string | undefined;
  // On a job page, an entry the parser could not pin to a matter defaults to
  // the matter being viewed — no need to type or pick it. Its phase-derived
  // fields (project code) are left undefined so the Log time dialog fills
  // them from that matter's status rather than the parser's no-matter guess.
  const usedFallback = !parsedJobId && !!fallbackJobId;
  return {
    jobId: parsedJobId ?? fallbackJobId,
    date: parsed.date,
    categoryId: parsed.categoryId as string | undefined,
    units: parsed.units,
    narrative: parsed.narrative,
    billable: parsed.billable,
    projectCode: usedFallback ? undefined : parsed.projectCode,
    unresolved: usedFallback
      ? parsed.unresolved.filter((field) => field !== "matter")
      : parsed.unresolved,
  };
}

export function AiQuickEntryDialog() {
  const { nlEntryOpen, closeNlEntry, openQuickEntryBatch } = useTimeTracker();
  const { activeOrg } = useActiveOrg();
  const orgId = activeOrg?._id;

  // The matter of the job page the user is on, if any. When Morgan cannot
  // work a matter out of the typed text, we fall back to it so logging time
  // against the matter you are reading needs no extra picking.
  const routeParams = useParams({ strict: false }) as { jobId?: string };
  const routeJobId = routeParams.jobId;

  if (!orgId) return null;

  return (
    <Dialog
      open={nlEntryOpen}
      onOpenChange={(next) => {
        if (!next) closeNlEntry();
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Quick entry</DialogTitle>
          <DialogDescription>
            Describe what you worked on and Morgan will code the matter,
            category and units. You review everything before it is saved.
          </DialogDescription>
        </DialogHeader>

        <NlEntryComposer
          orgId={orgId}
          baseDate={todayIso()}
          autoFocus
          submitLabel="Add"
          hint="One entry per line. Press Shift+Enter to add a new line."
          className="overflow-hidden rounded-lg border border-primary/20 ring-1 ring-primary/10"
          onParsed={(entries) => {
            closeNlEntry();
            openQuickEntryBatch(
              entries.map((entry) => toPrefill(entry, routeJobId)),
            );
            if (entries.length > 1) {
              toast.info(
                `${entries.length} entries parsed — review and save them one at a time.`,
              );
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
